import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Moon, Sun, User, X } from 'lucide-react';
import { ProfileProgress } from '../ui/ProfileProgress';
import { useSharedData } from '../../context/SharedDataContext';
import { BrandLogo } from '../BrandLogo';

export const Header: React.FC = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { flowProgress } = useSharedData();
  const hasStartedFlow = flowProgress.completedSteps.length > 0;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const isDark = localStorage.getItem('darkMode') === 'true';
    setDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', String(newDarkMode));
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const scrollToCalculators = () => {
    const target = document.getElementById('calculators');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCalculatorsClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      window.setTimeout(scrollToCalculators, 100);
    } else {
      scrollToCalculators();
    }
    setMobileOpen(false);
  };

  return (
    <header className="bg-white/85 dark:bg-slate-900/75 border-b border-slate-200/70 dark:border-slate-700/60 sticky top-0 z-50 backdrop-blur">
      <nav className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-lg font-bold text-primary-700 dark:text-primary-300">
            <BrandLogo size={26} className="text-primary-700 dark:text-primary-300" />
            <span className="font-display">Finance Compass</span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <button
              onClick={handleCalculatorsClick}
              className="text-sm text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Calculators
            </button>
            <Link
              to="/about"
              className="text-sm text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              About
            </Link>
            <Link
              to="/contact"
              className="text-sm text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Contact
            </Link>
            <Link
              to="/privacy"
              className="text-sm text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              Privacy
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <ProfileProgress />

            {hasStartedFlow && (
              <Link
                to="/dashboard"
                className="hidden sm:flex items-center gap-1 text-sm text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                title="View Dashboard"
              >
                <User className="w-4 h-4" />
                <span className="hidden lg:inline">Dashboard</span>
              </Link>
            )}

            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full hover:bg-slate-100/70 dark:hover:bg-slate-800/70 transition-colors"
              aria-label="Toggle dark mode"
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 rounded-full hover:bg-slate-100/70 dark:hover:bg-slate-800/70 transition-colors"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute top-0 right-0 h-full w-80 max-w-[85%] bg-white dark:bg-slate-900 shadow-2xl p-6 flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-lg font-bold text-primary-700 dark:text-primary-300">
                <BrandLogo size={24} className="text-primary-700 dark:text-primary-300" />
                <span className="font-display">Finance Compass</span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-full hover:bg-slate-100/70 dark:hover:bg-slate-800/70 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleCalculatorsClick}
                className="w-full text-left text-sm font-semibold text-gray-900 dark:text-gray-100"
              >
                Browse Calculators
              </button>
              <Link
                to="/about"
                onClick={() => setMobileOpen(false)}
                className="block text-sm text-gray-600 dark:text-gray-300"
              >
                About
              </Link>
              <Link
                to="/contact"
                onClick={() => setMobileOpen(false)}
                className="block text-sm text-gray-600 dark:text-gray-300"
              >
                Contact
              </Link>
              <Link
                to="/privacy"
                onClick={() => setMobileOpen(false)}
                className="block text-sm text-gray-600 dark:text-gray-300"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                onClick={() => setMobileOpen(false)}
                className="block text-sm text-gray-600 dark:text-gray-300"
              >
                Terms
              </Link>
              {hasStartedFlow && (
                <Link
                  to="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
                >
                  <User className="w-4 h-4" />
                  Dashboard
                </Link>
              )}
            </div>

            <div className="mt-auto border-t border-slate-200/70 dark:border-slate-700/60 pt-4 text-xs text-gray-500 dark:text-gray-400">
              Fast, private financial tools built for mobile.
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
